import { useEffect, useState } from 'react';
import { Image } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import './index.scss';

/* A 1x1 transparent gif. The <Image> below is never seen - it exists only to
   carry antd's preview overlay - so it is given a source that costs nothing
   rather than one of the real images, which would be a request per cell for a
   picture that is not being shown. The real one goes in `preview.src`. */
const BLANK =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * Names that stand in for their pictures: a list of labels, where a label with
 * an image behind it opens that image in antd's lightbox - the same overlay the
 * parts images column uses.
 *
 * Rows used to carry the pictures themselves, which made the row as tall as a
 * thumbnail even when it only had one word to say, and far taller when a part
 * fitted a dozen models. Text costs a line; the picture is a click away.
 *
 * The <Image> stays mounted for as long as the cell does. Antd settles its own
 * preview state when the component mounts, so one rendered only in response to
 * a click opens and closes again in the same tick.
 */
const PreviewableNames = ({ items, collapseAfter }) => {
  const { t } = useTranslation('common');
  const [previewSrc, setPreviewSrc] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const all = items?.filter(({ name }) => name) || [];

  /* Antd reuses row components by position, so a row left expanded would hand
     its expanded state to whatever part lands in that position on the next
     page. Collapse again whenever the names themselves change. */
  const identity = all.map(({ key, name }) => key ?? name).join('|');
  useEffect(() => setExpanded(false), [identity]);

  if (!all.length) return null;

  const collapsible = collapseAfter > 0 && all.length > collapseAfter;
  const shown = collapsible && !expanded ? all.slice(0, collapseAfter) : all;

  return (
    <div className="previewable-names">
      {shown.map(({ key, name, imageUrl }) =>
        imageUrl ? (
          /* A real button, so the picture is reachable by keyboard too - this
             is now the only way to it. */
          <button
            type="button"
            className="previewable-name previewable-name--has-image"
            key={key ?? name}
            onClick={() => setPreviewSrc(imageUrl)}
          >
            {name}
          </button>
        ) : (
          <span className="previewable-name" key={key ?? name}>
            {name}
          </span>
        ),
      )}
      {collapsible && (
        /* The count carries the information the hidden names would have: how
           many more this part fits. Long lists are why the row was tall. */
        <button
          type="button"
          className="previewable-name previewable-name--toggle"
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? t('show-less') : t('show-more-count', { count: all.length - collapseAfter })}
        </button>
      )}
      <Image
        src={BLANK}
        alt=""
        wrapperClassName="previewable-names__preview"
        preview={{
          visible: Boolean(previewSrc),
          src: previewSrc || BLANK,
          onVisibleChange: (visible) => !visible && setPreviewSrc(null),
        }}
      />
    </div>
  );
};

PreviewableNames.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      imageUrl: PropTypes.string,
    }),
  ),
  // Show at most this many names, then a control that reveals the rest.
  collapseAfter: PropTypes.number,
};

export default PreviewableNames;
